import { CloseOutlined, SettingOutlined, SmileOutlined } from '@ant-design/icons'
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { App, Modal, Tooltip } from 'antd'
import classNames from 'classnames'
import EmojiPickerReact, { EmojiClickData } from 'emoji-picker-react'
import React, { useCallback, useState } from 'react'

import { setFavoriteEmojis } from '@/redux/global'
import { useAppDispatch, useAppSelector } from '@/redux/utils'

import s from './emoji-picker.module.css'

export interface EmojiPickerProps {
  className?: string
  emojiGroupClassName?: string
  onEmojiSelected?: (emoji: string) => void
  maxEmojis?: number
}

const MAX_FAVORITE_EMOJIS = 40

export default function EmojiPicker({
  className,
  emojiGroupClassName,
  onEmojiSelected = () => { },
  maxEmojis,
}: EmojiPickerProps) {
  const favoriteEmojis = useAppSelector((state) => state.global.favoriteEmojis)
  const [sortedEmojis, setSortedEmojis] = useState(favoriteEmojis)
  const [isManageEmojiModalOpen, setIsManageEmojiModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { message } = App.useApp()

  const onSaveFavoriteEmojiChanges = useCallback(() => {
    dispatch(setFavoriteEmojis(sortedEmojis))
    setIsManageEmojiModalOpen(false)
    void message.success('Your favorite emojis have been successfully updated and added to the hotbar')
  }, [dispatch, message, sortedEmojis])

  const onCancelFavoriteEmojiChanges = useCallback(() => {
    setSortedEmojis(favoriteEmojis)
    setIsManageEmojiModalOpen(false)
  }, [favoriteEmojis])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSortedEmojis((items) => {
        const oldIndex = items.indexOf(active.id.toString())
        const newIndex = items.indexOf(over.id.toString())
        const movedEmojis = arrayMove(items, oldIndex, newIndex)
        return movedEmojis
      })
    }
  }, [])

  const handleRemoveEmoji = useCallback((emoji: string) => {
    setSortedEmojis((items) => (items.filter((item) => item !== emoji)))
  }, [])

  const handleAddFavoriteEmoji = useCallback(({ emoji }: EmojiClickData) => {
    if (sortedEmojis.some((item) => item === emoji)) {
      void message.error('Emoji already exists in your favorites. Please select a different emoji.')
    } else if (sortedEmojis.length === MAX_FAVORITE_EMOJIS) {
      void message.error('Maximum emoji limit reached. Please remove a favorite emoji before adding new ones.')
    } else {
      setSortedEmojis([...sortedEmojis, emoji])
    }
  }, [message, sortedEmojis])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
  )

  const DragableEmoji = ({ emoji }: { emoji: string }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: emoji })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    }

    return (
      <div
        className={s.DraggableEmoji}
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
      >
        {emoji}
        <CloseOutlined
          className={s.RemoveEmojiButton}
          onClick={() => handleRemoveEmoji(emoji)}
        />
      </div>
    )
  }

  return (
    <div className={classNames(s.EmojiPicker, className)}>
      <div className={classNames(s.EmojiGroup, emojiGroupClassName)}>
        <Tooltip
          classNames={{ root: s.Tooltip }}
          autoAdjustOverflow
          destroyTooltipOnHide
          title={
            <EmojiPickerReact
              className={s.EmojiPicker}
              skinTonesDisabled={true}
              previewConfig={{ showPreview: false }}
              onEmojiClick={({ emoji }) => onEmojiSelected(emoji)}
              width={420}
              height={320}
            />
          }
          trigger="click"
        >
          <SmileOutlined className={s.FavoriteEmoji} />
        </Tooltip>
        <SettingOutlined
          className={s.FavoriteEmoji}
          onClick={() => setIsManageEmojiModalOpen(true)}
        />
      </div>
      <div className={classNames(s.EmojiGroup, emojiGroupClassName)}>
        {
          favoriteEmojis
            .slice(0, maxEmojis ? maxEmojis : favoriteEmojis.length)
            .map((emoji, index) => (
              <div
                key={index}
                className={s.FavoriteEmoji}
                onClick={() => onEmojiSelected(emoji)}
              >
                {emoji}
              </div>
            ))
        }
      </div>

      <Modal
        title="Emoji keyboard"
        closeIcon={false}
        maskClosable={true}
        okText="Save"
        width={625}
        centered
        open={isManageEmojiModalOpen}
        onOk={onSaveFavoriteEmojiChanges}
        onCancel={onCancelFavoriteEmojiChanges}
      >
        <div className={s.EmojiKeyboard}>
          <div className={s.ManageFavoriteEmojis}>
            <span>Manage favorite emojis</span>
            {
              sortedEmojis.length > 0 ? (
                <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                  <SortableContext items={sortedEmojis}>
                    <div className={s.SortableEmojis} >
                      {
                        sortedEmojis.map((emoji, index) => (
                          <DragableEmoji key={index} emoji={emoji} />
                        ))
                      }
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className={s.AddFavoritesHint}>
                  Click an emoji to add to your favorites
                </div>
              )
            }
            <div className={s.FavoriteEmojisCount}>
              {sortedEmojis.length}/{MAX_FAVORITE_EMOJIS}
            </div>
          </div>
          <EmojiPickerReact
            width="100%"
            key={sortedEmojis.length}
            className={s.ManageEmojiPicker}
            skinTonesDisabled={true}
            previewConfig={{ showPreview: false }}
            onEmojiClick={handleAddFavoriteEmoji}
          />
        </div>
      </Modal>
    </div>
  )
}
